import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Heart, Leaf, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import BrandHeader from "@/components/BrandHeader";
import BrandFooter from "@/components/BrandFooter";

const IMG_ABOUT = "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?auto=format&fit=crop&w=1800&q=80";
const IMG_STORY = "https://images.unsplash.com/photo-1476611338391-6f395a0dd82e?auto=format&fit=crop&w=1400&q=80";
const EASE = [0.16, 1, 0.3, 1];

const values = [
  { Icon: Heart, title: "Progress over perfection", body: "We celebrate the small wins that actually change lives. Missing a day isn't failure — starting again is discipline." },
  { Icon: Leaf, title: "Whole-person wellness", body: "Real health touches every area — body, mind, relationships, finances, purpose, spirit, and the space you live in." },
  { Icon: Sparkles, title: "Kind, quiet, and useful", body: "No hype. No shame. Just real encouragement and practical tools that fit into a real life." },
];

export default function About() {
  return (
    <div className="min-h-screen bg-white text-[#333333]" data-testid="about-page">
      <BrandHeader variant="cream" />

      <section className="bg-[#F8F6F2] pt-16 pb-16 md:pt-24 md:pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#8FAF8A] mb-4">About</p>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-[4rem] font-medium tracking-tight text-[#333333] leading-[1.05]">
              Real transformation is <span className="italic">quiet.</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-[#4A4A4A] max-w-xl leading-relaxed">
              Wishing Wellness exists to encourage, educate, and equip everyday people to build healthier lives — not through hype, but through small choices made consistently.
            </p>
          </div>
          <div className="lg:col-span-5 relative hidden lg:block">
            <div className="rounded-[2rem] overflow-hidden shadow-[0_30px_80px_-30px_rgba(51,51,51,0.25)]">
              <img src={IMG_ABOUT} alt="Peaceful sunrise" className="w-full h-[440px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-14 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7, ease: EASE }}>
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#8FAF8A] mb-4">Our Story</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl font-medium tracking-tight text-[#333333] leading-[1.1]">
              Built for the <span className="italic">quiet middle.</span>
            </h2>
            <div className="mt-6 space-y-5 text-lg text-[#4A4A4A] leading-relaxed">
              <p>We started Wishing Wellness because we were tired of two extremes: performative wellness culture on one side, and the shame-based hustle culture on the other.</p>
              <p>Most of us aren&apos;t monks or marines. We&apos;re regular people trying to feel better, take care of our families, and become someone we&apos;re proud of — one ordinary day at a time.</p>
              <p>So we built a home for that middle. A trusted brand for whole-person wellness. Practical resources, beautiful journals, honest writing, and a community for the quiet work of getting better.</p>
              <p className="text-[#333333] font-semibold">No perfection. No unrealistic expectations. Just progress. Every single day.</p>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.97 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.9, ease: EASE }} className="relative">
            <div className="rounded-[2rem] overflow-hidden shadow-[0_30px_80px_-30px_rgba(51,51,51,0.2)]">
              <img src={IMG_STORY} alt="A calm walk on a wooded path" className="w-full h-[520px] object-cover" />
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-[#F8F6F2]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl mb-12">
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#8FAF8A] mb-4">What we believe</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl font-medium tracking-tight text-[#333333]">
              Three simple <span className="italic">values.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5 md:gap-6">
            {values.map(({ Icon, title, body }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.55, delay: (i % 3) * 0.06, ease: EASE }}
                className="bg-white rounded-3xl p-8 border border-[#EDE9E1]"
                data-testid={`value-${i + 1}`}
              >
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#8FAF8A]/12 text-[#6E8F6A]">
                  <Icon className="h-5.5 w-5.5" strokeWidth={1.75} />
                </span>
                <h3 className="mt-6 font-display text-xl font-bold text-[#333333]">{title}</h3>
                <p className="mt-2 text-[#4A4A4A] leading-relaxed">{body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <h2 className="font-serif-display text-3xl md:text-4xl font-medium tracking-tight text-[#333333]">
            Ready to take your first small step?
          </h2>
          <p className="mt-4 text-[#4A4A4A] text-lg">Start with our free 7-Day Discipline Challenge — the flagship program that opens the door to whole-person wellness.</p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
            <Button asChild className="rounded-full bg-[#333333] hover:bg-black text-white h-13 px-7 font-semibold" data-testid="about-primary-cta">
              <Link to="/the-strong-force-daily">Start The Strong Force Daily <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full bg-white border-[#EDE9E1] text-[#333333] hover:bg-[#F8F6F2] h-13 px-7 font-semibold" data-testid="about-secondary-cta">
              <Link to="/resources">Browse Resources</Link>
            </Button>
          </div>
        </div>
      </section>

      <BrandFooter />
    </div>
  );
}
